import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Plus, Megaphone, Play, Pause, Trash2, Users, MessageSquare, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TRIGGER_LABELS = {
  new_lead: "New Lead Added",
  stage_change: "Lead Stage Change",
  tag_applied: "Tag Applied",
  specific_date: "Specific Date",
  manual: "Manual Enrollment",
};

function StatusBadge({ status }) {
  const map = { active: "badge badge-active", paused: "badge badge-paused", stopped: "badge badge-stopped" };
  return <span className={map[status] || "badge badge-stopped"}>{status}</span>;
}

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/api/campaigns`)
      .then(r => setCampaigns(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/api/campaigns/${id}/status`, { status });
    load();
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    await axios.delete(`${API}/api/campaigns/${id}`);
    load();
  };

  return (
    <div className="p-4 md:p-8" data-testid="campaigns-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold font-heading text-slate-900">Campaigns</h1>
          <p className="text-slate-500 text-xs md:text-sm mt-0.5">{campaigns.length} automated SMS sequences</p>
        </div>
        <Link to="/campaigns/new" className="btn-primary" data-testid="create-campaign-btn">
          <Plus size={15} />
          New Campaign
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" /></div>
      ) : campaigns.length === 0 ? (
        <div className="card p-12 text-center" data-testid="empty-campaigns">
          <Megaphone size={32} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">No campaigns yet</p>
          <p className="text-slate-400 text-sm mt-1">Create your first automated SMS campaign</p>
          <Link to="/campaigns/new" className="btn-primary mt-4 inline-flex">
            <Plus size={15} /> Create Campaign
          </Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="campaign-list">
          {campaigns.map(c => (
            <div key={c.id} className="card p-4 md:p-5 hover:shadow-md transition-all duration-200" data-testid={`campaign-card-${c.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Megaphone size={16} className="text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900 font-heading text-sm sm:text-base">{c.name}</h3>
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 mt-1 text-xs sm:text-sm text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <MessageSquare size={12} />
                        {c.messages?.length || 0} msgs
                      </span>
                      <span className="flex items-center gap-1">
                        <Users size={12} />
                        {c.enrollment_count || 0} enrolled
                      </span>
                      <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full">
                        {TRIGGER_LABELS[c.trigger] || c.trigger}
                      </span>
                    </div>
                    {/* Message sequence preview */}
                    <div className="flex items-center gap-1.5 mt-2 overflow-x-auto pb-1">
                      {(c.messages || []).map((m, i) => (
                        <React.Fragment key={i}>
                          <div className="shrink-0 text-xs bg-[#FFF0EB] text-[#0F1419] border border-[#D1D5DB] rounded px-2 py-0.5 whitespace-nowrap">
                            {m.delay_hours === 0 ? "Immediately" : m.delay_hours < 24 ? `+${m.delay_hours}h` : `+${m.delay_hours / 24}d`}
                          </div>
                          {i < (c.messages || []).length - 1 && <ChevronRight size={12} className="text-slate-300 shrink-0" />}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0 ml-12 sm:ml-0">
                  <Link to={`/campaigns/${c.id}/edit`} className="btn-secondary py-1.5 px-3 text-xs" data-testid={`edit-campaign-${c.id}`}>Edit</Link>
                  {c.status === "active" ? (
                    <button onClick={() => updateStatus(c.id, "paused")} className="btn-secondary py-1.5 px-3 text-xs" data-testid={`pause-campaign-${c.id}`}>
                      <Pause size={12} /> Pause
                    </button>
                  ) : c.status === "paused" ? (
                    <button onClick={() => updateStatus(c.id, "active")} className="btn-secondary py-1.5 px-3 text-xs" data-testid={`resume-campaign-${c.id}`}>
                      <Play size={12} /> Resume
                    </button>
                  ) : null}
                  <button onClick={() => deleteCampaign(c.id)} className="btn-secondary py-1.5 px-2 text-xs text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-campaign-${c.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
